import { useState } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/shared/components';
import type { ObjectApartmentSummary, PropertyResponse } from '@/shared/api';
import { nestFlatFamilies } from '@/features/properties/flat-family';
import {
  ApartmentListItem,
  ApartmentTableRow,
  type ApartmentRowProps,
} from './ObjectApartmentRows';

interface ObjectApartmentTableProps {
  apartments: ObjectApartmentSummary[];
  detailById: Map<string, PropertyResponse>;
}

const capacity = (detail?: PropertyResponse) =>
  detail
    ? `${detail.occupancyAdults + detail.occupancyChildren} Schlafplätze · ${detail.bedrooms} Zi.`
    : '—';

/**
 * Die Apartments des Objekts. Ab 36 rem Breite des umgebenden Containers als Tabelle, darunter als
 * Liste — eine eigene Komposition statt einer gestauchten Tabelle (§9). Zimmer eines
 * Wohnungsverbunds stehen eingerückt unter ihrer Gesamtwohnung und lassen sich einklappen.
 */
export function ObjectApartmentTable({ apartments, detailById }: ObjectApartmentTableProps) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const rows = nestFlatFamilies(apartments);
  const roomCounts = new Map<string, number>();
  for (const row of rows) {
    const wholeFlatId = row.isRoom ? row.apartment.wholeFlatPropertyId : null;
    if (wholeFlatId) roomCounts.set(wholeFlatId, (roomCounts.get(wholeFlatId) ?? 0) + 1);
  }
  const toggle = (id: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const visible = rows
    .filter((row) => !row.isRoom || !collapsed.has(row.apartment.wholeFlatPropertyId ?? ''))
    .map(
      (row): ApartmentRowProps => ({
        row,
        roomCount: roomCounts.get(row.apartment.id) ?? 0,
        open: !collapsed.has(row.apartment.id),
        onToggle: () => toggle(row.apartment.id),
        capacity: capacity(detailById.get(row.apartment.id)),
      }),
    );

  return (
    <>
      <div className="@xl:block hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Apartment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Kapazität</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((props) => (
              <ApartmentTableRow key={props.row.apartment.id} {...props} />
            ))}
          </TableBody>
        </Table>
      </div>
      <ul className="@xl:hidden border-line/50 bg-card divide-line/60 divide-y rounded-lg border">
        {visible.map((props) => (
          <ApartmentListItem key={props.row.apartment.id} {...props} />
        ))}
      </ul>
    </>
  );
}
